/* eslint-disable dot-notation */
import { defineStore } from 'pinia'
import ApiService, { API_URL } from '@/core/ApiService'
import ApiError from '@/core/ApiError'
import { decryptWithPassport, encryptWithPassport, stableStringify } from '@/utils/cryptoUtils'
import { AlipayUser, Country, Dialog, PassportData, RefundSlip, RemitInfo } from '@/types'
import router from '@/router'
import i18n from '@/core/i18n'
import PassportDBService from '@/core/PassportDBService'

export const useStore = defineStore('refund', {
  state: () => (
    {
      signature: '',
      saveTime: 0,
      ocrPassportData: null as PassportData|null,
      passportData: null as PassportData|null,
      countrycode: [] as Country[],
      refundSlipList: [] as RefundSlip[],
      scannedRefundSlipList: [] as RefundSlip[],
      alipayList: [] as AlipayUser[],
      remitInfo: null as RemitInfo|null,
      remitInfoList: [] as RemitInfo[],
      cityDoneList: [] as RefundSlip[],
      terminalDoneList: [] as RefundSlip[],
      doneTime: 0
    }
  ),
  actions: {
    // 국적 코드 리스트 요청
    async fetchCountryCode () {
      try {
        const response = await ApiService.post(API_URL.GET_COUNTRY_CODE, {})

        const countryList = response.data.country_list
        if (countryList === undefined || countryList.length === 0) {
          throw new ApiError('901', response.statusText)
        }
        this.countrycode = countryList
      } catch (e: any) {
        if (e instanceof ApiError) {
          throw e
        } else {
          throw new ApiError('901', e.message)
        }
      }
    },
    // 여권 ocr 요청
    async fetchPassportOcr (formData: FormData) {
      try {
        this.ocrPassportData = null

        const response = await ApiService.uploadImage(API_URL.PASSPORT_OCR, formData)

        // 정상 응답
        const responseCode = response.data['response_code']
        const responseMessage = response.data['response_message']

        if (responseCode === '100') {
          this.ocrPassportData = {
            passportNo: response.data['passport_no'],
            passportName: response.data['passport_name'],
            nationalityCode: response.data['nationality_code'],
            dateOfBirth: response.data['date_of_birth'],
            dateOfExpiry: response.data['expiry_date'],
            gender: response.data['gender'],
            nationalityCodeModified: response.data['nationality_modified'],
            ocrType: response.data['ocr_type']
          }
        } else if (['200', '300'].includes(responseCode)) {
          throw new ApiError('901', responseMessage)
        } else if (responseCode) {
          throw new ApiError(responseCode, responseMessage)
        }

        return this.ocrPassportData
      } catch (e: any) {
        if (e instanceof ApiError) {
          throw e
        } else {
          throw new ApiError('901', e.message)
        }
      }
    },
    // 환급 수단 미등록 전표 조회
    async fetchUnbookedRefunds () {
      try {
        this.refundSlipList = []

        if (!this.passportData) {
          router.push('/' + i18n.global.locale.value)
          return
        }

        const response = await ApiService.post(API_URL.GET_UNBOOKED_REFUNDS, {
          passport_no: this.passportData.passportNo,
          passport_name: this.passportData.passportName,
          nationality_code: this.passportData.nationalityCode,
          date_of_birth: this.passportData.dateOfBirth,
          expiry_date: this.passportData.dateOfExpiry,
          gender: this.passportData.gender
        })

        const responseCode = response.data['response_code']
        const responseMessage = response.data['response_message']

        if (responseCode === '100') {
          const rawData = response.data['refund_slip_list'] as []

          this.refundSlipList = rawData.map(data => ({
            buySerialNo: data['buy_serial_no'],
            sellDate: data['sell_date'],
            totalSalesAmt: data['total_sales_amt'],
            totalRefundAmt: data['total_refund_amt'],
            slipStatusCode: data['slip_status_code']
          }))
        } else if (['200', '300'].includes(responseCode)) {
          throw new ApiError('901', responseMessage)
        } else if (responseCode) {
          throw new ApiError(responseCode, responseMessage)
        }
      } catch (e: any) {
        if (e instanceof ApiError) {
          throw e
        } else {
          throw new ApiError('901', e.message)
        }
      }
    },
    // 미반출 전표 조회
    async fetchUnexportedRefunds () {
      try {
        if (!this.passportData) {
          router.push('/' + i18n.global.locale.value)
          return
        }

        const response = await ApiService.post(API_URL.GET_UNEXPORTED_REFUNDS, {
          passport_no: this.passportData.passportNo,
          passport_name: this.passportData.passportName,
          nationality_code: this.passportData.nationalityCode,
          date_of_birth: this.passportData.dateOfBirth,
          expiry_date: this.passportData.dateOfExpiry,
          gender: this.passportData.gender
        })

        const responseCode = response.data['response_code']
        const responseMessage = response.data['response_message']

        if (responseCode === '100') {
          const rawData = response.data['refund_slip_list'] as []

          this.refundSlipList = rawData.map(data => ({
            buySerialNo: data['buy_serial_no'],
            sellDate: data['sell_date'],
            slipStatusCode: data['slip_status_code'],
            totalSalesAmt: data['total_sales_amt'],
            totalRefundAmt: data['total_refund_amt'],
            refundWayCode: data['refund_way_code'],
            refundWayName: data['refund_way_name'],
            remitBookId: data['remit_book_id'],
            remitBookInfo: data['remit_book_info']
          }))
        } else if (['200', '300'].includes(responseCode)) {
          throw new ApiError('901', responseMessage)
        } else if (responseCode) {
          throw new ApiError(responseCode, responseMessage)
        }
      } catch (e: any) {
        if (e instanceof ApiError) {
          throw e
        } else {
          throw new ApiError('901', e.message)
        }
      }
    },
    // 전표 매핑 요청
    async receiptMapping (qrData: string) {
      try {
        if (!this.passportData) {
          router.push('/' + i18n.global.locale.value)
          return
        }

        const response = await ApiService.post(API_URL.RECEIPT_MAPPING, {
          buy_serial_no: qrData,
          passport_no: this.passportData.passportNo,
          passport_name: this.passportData.passportName,
          nationality_code: this.passportData.nationalityCode,
          date_of_birth: this.passportData.dateOfBirth,
          expiry_date: this.passportData.dateOfExpiry,
          gender: this.passportData.gender
        })

        const responseCode = response.data['response_code']
        const responseMessage = response.data['response_message']

        if (responseCode === '100') {
          const buySerialNo = response.data['buy_serial_no']
          const newList = [...this.scannedRefundSlipList].filter(s => s.buySerialNo !== buySerialNo)
          newList.push({
            buySerialNo: response.data['buy_serial_no'],
            sellDate: response.data['sell_date'],
            totalSalesAmt: response.data['total_sales_amt'],
            totalRefundAmt: response.data['total_refund_amt'],
            refundWayName: response.data['refund_way_name'],
            refundWayCode: response.data['refund_way_code'],
            remitBookId: response.data['remit_book_id'],
            remitBookInfo: response.data['remit_book_info']
          })

          this.scannedRefundSlipList = newList
        } else if (responseCode === '002') {
          throw new ApiError('002', response.data['passport_no_org'])
        } else if (['200', '300'].includes(responseCode)) {
          throw new ApiError('901', responseMessage)
        } else if (responseCode) {
          throw new ApiError(responseCode, responseMessage)
        }
      } catch (e: any) {
        if (e instanceof ApiError) {
          throw e
        } else {
          throw new ApiError('901', e.message)
        }
      }
    },
    // 거래 내역 조회
    async fetchRefundHistory () {
      try {
        this.refundSlipList = []

        if (!this.passportData) {
          router.push('/' + i18n.global.locale.value)
          return
        }

        const response = await ApiService.post(API_URL.GET_REFUND_HISTORY, {
          passport_no: this.passportData.passportNo,
          passport_name: this.passportData.passportName,
          nationality_code: this.passportData.nationalityCode,
          date_of_birth: this.passportData.dateOfBirth,
          expiry_date: this.passportData.dateOfExpiry,
          gender: this.passportData.gender
        })

        const responseCode = response.data['response_code']
        const responseMessage = response.data['response_message']

        if (responseCode === '100') {
          const rawData = response.data['refund_slip_list'] as []

          this.refundSlipList = rawData.map(data => ({
            buySerialNo: data['buy_serial_no'],
            sellDate: data['sell_date'],
            totalSalesAmt: data['total_sales_amt'],
            totalRefundAmt: data['total_refund_amt'],
            benefitAmt: data['benefit_amt'],
            slipStatusCode: data['slip_status_code'],
            // accidentTypeCode: data['accident_type_code'],
            refundWayCode: data['refund_way_code'],
            refundWayName: data['refund_way_name'],
            remitBookId: data['remit_book_id'],
            remitBookInfo: data['remit_book_info'],
            outDivisionCode: data['out_division_code'],
            customsOutNo: data['customs_out_no'],
            outDate: data['out_date']
          }))
        } else if (['200', '300'].includes(responseCode)) {
          throw new ApiError('901', responseMessage)
        } else if (responseCode) {
          throw new ApiError(responseCode, responseMessage)
        }
      } catch (e: any) {
        if (e instanceof ApiError) {
          throw e
        } else {
          throw new ApiError('901', e.message)
        }
      }
    },
    // 송금 예약 요청
    async bookRefunds () {
      try {
        if (this.doneTime !== 0) {
          return
        }

        this.cityDoneList = []

        // 여권 정보가 없을 경우 시스템 에러
        if (!this.passportData) {
          throw new ApiError('901', 'Refund information error.')
        }

        // 개인정보 동의 서명이 없을 경우 시스템 에러
        if (!this.signature) {
          throw new ApiError('901', 'Refund information error.')
        }

        /**
         * 송금 예약 정보(환급 수단) 필요 여부 체크 플래그
         * 기송금예약건만 존재할 경우 remitBookId가 불필요
         */
        let needRemitBookId = false

        /**
         * 거래 리스트 중 중복 제거
         * 송금 예약 정보(환급 수단)이 한건이라도 필요할 경우 flag ON
         */
        const buySerialNoList = Array.from(
          new Set(
            this.refundSlipList
              .concat(this.scannedRefundSlipList)
              .map(r => {
                // 송금 예약이 필요한 건이 하나라도 있을경우 체크
                if (!r.remitBookId) {
                  needRemitBookId = true
                }

                return r.buySerialNo
              })
          )
        )

        // 송금 예약 정보가 필요하지만 null인 경우 시스템 에러
        if (needRemitBookId && (this.remitInfo === null || this.remitInfo.remitBookId === null)) {
          throw new ApiError('901', 'Refund method information could not be found.')
        }

        // sectiontype 체크
        const route = router.currentRoute.value
        const sectiontype = route.query.sectiontype as string || undefined

        const response = await ApiService.post(API_URL.BOOK_REFUNDS, {
          sign_data: this.signature,
          passport_no: this.passportData.passportNo,
          passport_name: this.passportData.passportName,
          nationality_code: this.passportData.nationalityCode,
          date_of_birth: this.passportData.dateOfBirth,
          expiry_date: this.passportData.dateOfExpiry,
          gender: this.passportData.gender,
          refund_way_code: this.remitInfo?.refundWayCode,
          remit_book_id: this.remitInfo?.remitBookId,
          remit_book_info: this.remitInfo?.remitBookInfo,
          remit_book_country_num: this.remitInfo?.remitBookCountryNum,
          buy_serial_no_list: buySerialNoList,
          section_type: sectiontype,
          input_type: this.passportData.inputType,
          ocr_type: this.passportData.ocrType
        })

        const responseCode = response.data['response_code']
        const responseMessage = response.data['response_message']

        if (responseCode === '100') {
          const rawData = response.data['refund_slip_list'] as []
          if (rawData.length > 0) {
            this.cityDoneList = rawData.map(data => ({
              buySerialNo: data['buy_serial_no'],
              sellDate: data['sell_date'],
              totalSalesAmt: data['total_sales_amt'],
              totalRefundAmt: data['total_refund_amt'],
              benefitAmt: data['benefit_amt'],
              slipStatusCode: data['slip_status_code'],
              refundWayCode: data['refund_way_code'],
              refundWayName: data['refund_way_name'],
              remitBookId: data['remit_book_id'],
              remitBookInfo: data['remit_book_info']
            }))

            this.doneTime = new Date().getTime()
          } else {
            throw new ApiError('901', responseMessage)
          }
        } else if (['200', '300'].includes(responseCode)) {
          throw new ApiError('901', responseMessage)
        } else if (responseCode) {
          throw new ApiError(responseCode, responseMessage)
        }
      } catch (e: any) {
        if (e instanceof ApiError) {
          throw e
        } else {
          throw new ApiError('901', e.message)
        }
      }
    },
    // 반출 요청
    async outRefunds () {
      try {
        if (this.doneTime !== 0) {
          return
        }

        this.terminalDoneList = []

        // 여권 정보가 없을 경우 시스템 에러
        if (!this.passportData) {
          throw new ApiError('901', 'Refund information error.')
        }

        // 개인정보 동의 서명이 없을 경우 시스템 에러
        if (!this.signature) {
          throw new ApiError('901', 'Refund information error.')
        }

        /**
         * 송금 예약 정보(환급 수단) 필요 여부 체크 플래그
         * 기송금예약건만 존재할 경우 remitBookId가 불필요
         */
        let needRemitBookId = false

        /**
         * 거래 리스트 중 중복 제거
         * 거래 중 환급전 + 송금예약 미등록 건이 있을 경우 flag ON
         */
        // 중복 제거
        const buySerialNoList = Array.from(
          new Set(
            this.refundSlipList
              .concat(this.scannedRefundSlipList)
              .map(r => {
                // 송금 예약이 필요한 건이 하나라도 있을경우 체크
                if (!r.remitBookId && r.slipStatusCode === '01') {
                  needRemitBookId = true
                }

                return r.buySerialNo
              })
          )
        )

        // 송금 예약 정보가 필요하지만 null인 경우 시스템 에러
        if (needRemitBookId && (this.remitInfo === null || this.remitInfo.remitBookId === null)) {
          throw new ApiError('901', 'Refund method information could not be found.')
        }

        // sectiontype 체크
        const route = router.currentRoute.value
        const sectiontype = route.query.sectiontype as string || undefined

        const response = await ApiService.post(API_URL.OUT_REQUEST, {
          sign_data: this.signature,
          passport_no: this.passportData.passportNo,
          passport_name: this.passportData.passportName,
          nationality_code: this.passportData.nationalityCode,
          date_of_birth: this.passportData.dateOfBirth,
          expiry_date: this.passportData.dateOfExpiry,
          gender: this.passportData.gender,
          refund_way_code: this.remitInfo?.refundWayCode,
          remit_book_id: this.remitInfo?.remitBookId,
          remit_book_info: this.remitInfo?.remitBookInfo,
          remit_book_country_num: this.remitInfo?.remitBookCountryNum,
          buy_serial_no_list: buySerialNoList,
          section_type: sectiontype,
          input_type: this.passportData.inputType,
          ocr_type: this.passportData.ocrType
        })

        const responseCode = response.data['response_code']
        const responseMessage = response.data['response_message']

        if (responseCode === '100') {
          const rawData = response.data['refund_slip_list'] as []

          this.terminalDoneList = rawData.map(data => ({
            buySerialNo: data['buy_serial_no'],
            sellDate: data['sell_date'],
            totalSalesAmt: data['total_sales_amt'],
            totalRefundAmt: data['total_refund_amt'],
            benefitAmt: data['benefit_amt'],
            refundWayCode: data['refund_way_code'],
            refundWayName: data['refund_way_name'],
            slipStatusCode: data['slip_status_code'],
            remitBookId: data['remit_book_id'],
            remitBookInfo: data['remit_book_info'],
            outDate: data['out_date'],
            outDivisionCode: data['out_division_code'],
            customsOutNo: data['customs_out_no']
          }))

          this.doneTime = new Date().getTime()
        } else if (['200', '300'].includes(responseCode)) {
          throw new ApiError('901', responseMessage)
        } else if (responseCode) {
          throw new ApiError(responseCode, responseMessage)
        }
      } catch (e: any) {
        if (e instanceof ApiError) {
          throw e
        } else {
          throw new ApiError('901', e.message)
        }
      }
    },
    // 알리페이 아이디 조회
    async fetchAlipayList (phone: string) {
      try {
        this.alipayList = []

        const response = await ApiService.post(API_URL.SEARCH_ALIPAY_ID, {
          alipay_key: phone,
          alipay_type: '1'
        })

        // 성공 시
        const responseCode = response.data['response_code']
        const responseMessage = response.data['response_message']

        if (responseCode === '100') {
          const rawData = response.data['alipay_user_list'] as []

          this.alipayList = rawData.map(data => ({
            alipayLoginId: data['alipay_login_id'],
            alipayId: data['alipay_id'],
            alipayUsername: data['alipay_user_name']
          }))
        } else if (['200', '300'].includes(responseCode)) {
          throw new ApiError('901', responseMessage)
        } else if (responseCode) {
          throw new ApiError(responseCode, responseMessage)
        }
      } catch (e: any) {
        if (e instanceof ApiError) {
          throw e
        } else {
          throw new ApiError('901', e.message)
        }
      }
    },
    // IndexedDB에 저장된 환급 수단 목록 조회
    async fetchRemitInfoList () {
      this.remitInfoList = []
      if (!this.passportData) {
        router.push('/' + i18n.global.locale.value)
        return
      }

      // indexeddb에서 여권 정보 및 환급 수단 정보 조회
      const result = await PassportDBService.getInstance().findFirst() as any

      if (result) {
        // indexeddb에 여권 정보가 존재할 경우
        const encryptedRemitInfoList = result.remitInfoList
        if (Array.isArray(encryptedRemitInfoList) && encryptedRemitInfoList.length > 0) {
          // 환급 수단 정보가 정보가 존재할 경우 복호화 하여 store에 저장
          const passportJson = stableStringify(this.passportData)
          const newRemitInfoList: RemitInfo[] = []
          for (const info of encryptedRemitInfoList) {
            newRemitInfoList.push(JSON.parse(await decryptWithPassport(passportJson, info)) as RemitInfo)
          }
          this.remitInfoList = newRemitInfoList
        }
      }
    },
    // IndexedDB에 환급 수단 저장
    async addRemitInfoToList (data: RemitInfo) {
      this.remitInfoList = []

      if (!this.passportData) {
        router.push('/' + i18n.global.locale.value)
        return
      }

      // indexeddb에서 여권 정보 및 환급 수단 정보 조회
      const result = await PassportDBService.getInstance().findFirst() as any

      if (result) {
        // 환급 수단 암호화 하여 추가
        const passportJson = stableStringify(this.passportData)

        const decryptedRemitInfoList: any[] = []
        const encryptedRemitInfo = await encryptWithPassport(passportJson, JSON.stringify(data))

        let newRemitInfoList: string[] = []
        if (Array.isArray(result.remitInfoList)) {
          // 중복 값 체크를 위해 복호화
          for (const encrypted of result.remitInfoList) {
            const decrypted = await decryptWithPassport(passportJson, encrypted) as any
            decryptedRemitInfoList.push(JSON.parse(decrypted))
          }

          // 중복 값이 없을 때 만 값 추가
          if (decryptedRemitInfoList.every(info => info.remitBookId !== data.remitBookId)) {
            newRemitInfoList = [...result.remitInfoList, encryptedRemitInfo]
          } else {
            newRemitInfoList = [...result.remitInfoList]
          }
        } else {
          // indexed db에 값이 없거나 배열이 아닌 값이 있을 경우 배열 값으로 덮어 씌움
          newRemitInfoList = [encryptedRemitInfo]
        }
        await PassportDBService.getInstance().put(result.id, { ...result, remitInfoList: newRemitInfoList })
      }
    },
    // IndexedDB에 저장된 환급 수단 제거
    async removeRemitInfoFromList (idx: number) {
      // indexeddb에서 여권 정보 및 환급 수단 정보 조회
      const result = await PassportDBService.getInstance().findFirst() as any

      if (result && Array.isArray(result.remitInfoList) && result.remitInfoList.length > idx) {
        const newRemitInfoList = [...result.remitInfoList]
        newRemitInfoList.splice(idx, 1)
        await PassportDBService.getInstance().put(result.id, { ...result, remitInfoList: newRemitInfoList })
      }
    },
    // 사인 데이터 및 사인 시점 저장
    setSignature (data: string) {
      this.signature = data
      this.saveTime = new Date().getTime()
    },
    // 여권 정보 설정
    setPassportData (data: PassportData) {
      this.passportData = data
    },
    setOcrPassportData (data: PassportData) {
      this.ocrPassportData = data
    },
    // ocr 여권 데이터 제거
    clearOcrPassportData () {
      this.ocrPassportData = null
    },
    // 여권 정보 제거
    clearPassportData () {
      this.passportData = null
    },
    // 알리페이 조회 결과 제거
    clearAlipayList () {
      this.alipayList = []
    },
    // 환급 수단 설정
    setRemitInfo (data: RemitInfo|null) {
      this.remitInfo = data
    },
    clearReceiptList () {
      this.refundSlipList = []
      this.scannedRefundSlipList = []
    },
    // 송금 예약 완료/반출 완료 데이터 제거
    clearDoneList () {
      this.doneTime = 0
      this.cityDoneList = []
      this.terminalDoneList = []
    },
    $reset () {
      this.signature = ''
      this.saveTime = 0
      this.ocrPassportData = null
      this.passportData = null
      this.countrycode = []
      this.refundSlipList = []
      this.scannedRefundSlipList = []
      this.alipayList = []
      this.remitInfo = null
      this.remitInfoList = []
      this.cityDoneList = []
      this.terminalDoneList = []
      this.doneTime = 0
    }
  },
  persist: {
    storage: sessionStorage
  }
})
